import { PropsWithChildren, ReactElement, useMemo } from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { buttonTextTypes, buttonTextVariants, DSButtonType } from '../../design-system/Button'
import React from 'react'
import { ActionType } from '@ppwr/convex/shared/types'
import { DS } from '../../design-system'
import { translate } from '../../i18n'

type ColorAndIcon = {
  icon: ReactElement
  type: DSButtonType
}

const ActionColorAndIcons: Partial<Record<ActionType, ColorAndIcon>> = {
  allin: {
    icon: <DS.Icon name="Arrow--top-right" size={16} color="Global/White" />,
    type: 'On Light'
  },
  bet: {
    icon: <DS.Icon name="Arrow--top-right" size={16} color="Global/White" />,
    type: 'On Light'
  },
  call: {
    icon: <DS.Icon name="Arrow---right" size={16} color="Global/Black" />,
    type: 'On Dark'
  },
  check: {
    icon: <DS.Icon name="Arrow---right" size={16} color="Global/Black" />,
    type: 'On Dark'
  },
  fold: {
    icon: <DS.Icon name="Arrow--bottom-right" size={16} color="Global/White" />,
    type: 'On Light'
  },
  raise: {
    icon: <DS.Icon name="Arrow--top-right" size={16} color="Global/White" />,
    type: 'On Light'
  }
}
const getActionText = (action: ActionType) => {
  return translate(`newGameScreen.player.actionHints.${action.toUpperCase()}`)
}

const CallAmountIndicator = ({ children, onPress }: PropsWithChildren<{ onPress: () => void }>) => (
  <View style={[styles.callChipsContainer]}>
    <TouchableOpacity onPress={onPress} style={styles.callChips}>
      <DS.Icon name="Chip" size={10} color="Global/Black" />
      <DS.Text variant="Caption/Bold" color="Brand/Green">
        {children}
      </DS.Text>
    </TouchableOpacity>
  </View>
)

interface PlayerActionsProps {
  actions: {
    type: ActionType
    amount?: number
  }[]
  onPress: (action: ActionType) => void
  disabledActions?: ActionType[]
}

export const PlayerActions = ({ actions, onPress, disabledActions = [] }: PlayerActionsProps) => {
  const shouldHideAllIn = actions.some((a) => a.type === 'bet' || a.type === 'raise')
  const filteredActions = shouldHideAllIn ? actions.filter((a) => a.type !== 'allin') : actions
  // Put Call in the middle of the actions (index 1)
  const callActionIndex = filteredActions.findIndex((a) => a.type === 'call')
  const sortedActions = useMemo(() => {
    if (callActionIndex === -1 || filteredActions.length < 2) {
      // No call action or not enough actions to reorder
      return filteredActions
    }

    // Move call action to index 1
    const result = filteredActions.slice()
    const [callAction] = result.splice(callActionIndex, 1)
    result.splice(1, 0, callAction)
    return result
  }, [callActionIndex, filteredActions])

  return (
    <View style={styles.container}>
      {sortedActions.map((action, idx) => {
        const actionConfig = ActionColorAndIcons[action.type] || { type: 'On Light' }
        return (
          <React.Fragment key={`action-${action}-${idx}`}>
            <DS.ButtonSimple
              variant="Large/Primary"
              type={actionConfig.type}
              onPress={() => onPress(action.type)}
              disabled={disabledActions.includes(action.type)}
              containerStyle={[
                action.type === 'fold' ? { backgroundColor: DS.palette['Brand/Maroon'] } : {},
                { flex: 1 },
                disabledActions.includes(action.type) ? { opacity: 0.4 } : {}
              ]}
            >
              <DS.Center style={{ flexGrow: 1, flexDirection: 'row', gap: 4 }}>
                <DS.Text
                  color={buttonTextTypes[actionConfig.type]}
                  variant={buttonTextVariants['Large/Primary']}
                  numberOfLines={1}
                >
                  {getActionText(action.type)}
                </DS.Text>
                {ActionColorAndIcons[action.type]?.icon}
              </DS.Center>
              {action.type === 'call' ? (
                <CallAmountIndicator onPress={() => onPress('call')}>{action.amount}</CallAmountIndicator>
              ) : null}
            </DS.ButtonSimple>
          </React.Fragment>
        )
      })}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    gap: DS.spacing[8],
    alignItems: 'center',
    width: '100%'
  },
  callChipsContainer: {
    position: 'absolute',
    zIndex: 1,
    left: '50%',
    marginLeft: -12,
    bottom: -12
  },
  callChips: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 48,
    borderColor: DS.palette['Brand/Light Grey'],
    borderWidth: 1,
    flexDirection: 'row',
    gap: 4,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: DS.palette['Global/White']
  }
})
